import { useState } from 'react';

// Operator guide for the Phase 2a infrastructure tools. Rendered in-app from
// the "?" button in the top toolbar so an operator can read it without
// leaving the map. The same content also lives at docs/operator-guide-fa.md
// for offline reference / training material.

const BACKDROP = {
  position: 'fixed',
  inset: 0,
  zIndex: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(2,6,23,0.6)',
  backdropFilter: 'blur(3px)',
  WebkitBackdropFilter: 'blur(3px)',
  direction: 'rtl',
};
const CARD = {
  width: 'min(720px, 94vw)',
  maxHeight: '88vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  padding: '22px 26px',
  background: 'linear-gradient(135deg, rgba(15,23,42,0.97) 0%, rgba(30,41,59,0.95) 100%)',
  borderRadius: 14,
  border: '1px solid rgba(148,163,184,0.26)',
  borderTop: '3px solid #0d9488',
  boxShadow:
    '0 1px 0 rgba(255,255,255,0.07) inset, 0 18px 40px rgba(2,6,23,0.7)',
  color: '#e2e8f0',
  fontFamily: "'Vazirmatn', sans-serif",
};
const H1 = {
  fontSize: 18,
  fontWeight: 800,
  color: '#5eead4',
  lineHeight: 1.3,
};
const H2 = {
  fontSize: 14.5,
  fontWeight: 700,
  color: '#a7f3d0',
  marginTop: 8,
  lineHeight: 1.4,
};
const P = {
  fontSize: 13,
  color: '#cbd5e1',
  lineHeight: 1.85,
};
const UL = {
  margin: '4px 0 0',
  paddingInlineStart: 20,
  fontSize: 13,
  color: '#cbd5e1',
  lineHeight: 1.85,
};
const Tag = ({ color = '#0d9488', children }) => (
  <span style={{
    display: 'inline-block',
    padding: '1px 7px',
    margin: '0 2px',
    fontSize: 12,
    fontWeight: 700,
    background: color,
    color: '#f0fdfa',
    borderRadius: 4,
    lineHeight: 1.6,
  }}>{children}</span>
);

export default function OperatorGuideModal({ onClose }) {
  const [tab, setTab] = useState('intro');
  const tabs = [
    { id: 'intro', label: 'مقدمه' },
    { id: 'building', label: 'ساختمان' },
    { id: 'kml', label: 'بارگذاری KML' },
    { id: 'street', label: 'خیابان' },
    { id: 'entry', label: 'نقطه‌ی ورود' },
    { id: 'obstacle', label: 'مانع' },
    { id: 'pole', label: 'تیر' },
    { id: 'tips', label: 'اصول کیفیت' },
  ];
  return (
    <div style={BACKDROP} role="dialog" aria-modal="true" aria-label="راهنمای اپراتور">
      <div style={CARD}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={H1}>راهنمای اپراتور - ابزارهای مسیریابی</div>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '6px 12px',
              background: 'rgba(51,65,85,0.6)',
              color: '#e2e8f0',
              fontFamily: "'Vazirmatn', sans-serif",
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid rgba(148,163,184,0.32)',
              borderRadius: 7,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            بستن
          </button>
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {tabs.map((t) => {
            const sel = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  padding: '5px 11px',
                  background: sel ? '#0d9488' : 'rgba(51,65,85,0.5)',
                  color: sel ? '#f0fdfa' : '#cbd5e1',
                  fontFamily: "'Vazirmatn', sans-serif",
                  fontSize: 11.5,
                  fontWeight: sel ? 700 : 600,
                  border: `1px solid ${sel ? 'rgba(45,212,191,0.4)' : 'rgba(148,163,184,0.28)'}`,
                  borderRadius: 16,
                  cursor: 'pointer',
                  transition: 'background 140ms ease',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'intro' && (
          <>
            <p style={P}>
              هدف ما این است که کابلِ فیبر را در شهر طوری مسیریابی کنیم که از خیابان‌های واقعی برود، از وسطِ ساختمان رد نشود، از معابرِ ممنوعه رد نشود و در صورتِ وجودِ تیرِ هوایی، از تیرها استفاده کند. برای این کار، موتورِ مسیریاب به یک «نقشه» از زیرساختِ منطقه نیاز دارد. شما با ابزارهای این صفحه آن نقشه را می‌سازید.
            </p>
            <p style={P}>
              هر چه دقیق‌تر و کامل‌تر زیرساخت را وارد کنید، مسیری که سامانه پیشنهاد می‌دهد به مسیرِ واقعیِ نقشه‌کشِ انسانی نزدیک‌تر می‌شود. شش ابزار در اختیارِ شما هست:
            </p>
            <ul style={UL}>
              <li><b>رسم ساختمان</b>: پولیگانِ ساختمان‌ها.</li>
              <li><b>بارگذاری KML</b>: ساختمان‌هایی که قبلاً در Google Earth کشیده‌اید را به‌صورتِ گروهی وارد کنید.</li>
              <li><b>نقطه‌ی ورود</b>: روی ساختمان مشخص کنید کابل از کدام لبه وارد می‌شود.</li>
              <li><b>رسم خیابان</b>: خیابان‌ها و کوچه‌هایی که در نقشه‌ی پایه نیستند یا اشتباه‌اند.</li>
              <li><b>منطقه‌ی عبورناپذیر</b>: رودخانه، ریل، بزرگراه، محدوده‌ی ممنوع.</li>
              <li><b>تیر/پایه</b>: موقعیتِ تیرهای برق/مخابرات برای فیبرِ هوایی.</li>
            </ul>
          </>
        )}

        {tab === 'building' && (
          <>
            <div style={H2}>رسمِ پولیگانِ ساختمان</div>
            <ul style={UL}>
              <li>روی <Tag>رسم ساختمان</Tag> کلیک کنید.</li>
              <li>روی هر گوشه‌ی ساختمان یک کلیک بزنید (حداقل سه گوشه).</li>
              <li>وقتی تمام شد روی <Tag>اتمام</Tag> بزنید، سپس روی <Tag>ذخیره</Tag>.</li>
              <li>اگر می‌خواهید گوشه‌ی آخر را پاک کنید، روی <Tag color="#475569">حذف نقطه</Tag> بزنید.</li>
            </ul>
            <p style={P}>
              اگر شکلی که می‌کشید با یک ساختمانِ موجود همپوشانی داشته باشد، در همان لحظه قرمز می‌شود و دکمه‌ی اتمام غیرفعال است. اول شکل را اصلاح کنید.
            </p>
            <p style={P}>
              <b>ویرایش:</b> روی ساختمانِ خودتان (یا اگر ادمین هستید، روی هر ساختمان) کلیک کنید تا گوشه‌ها قابلِ کشیدن شوند، سپس روی <Tag color="#d97706">ذخیره تغییر</Tag> بزنید.
            </p>
          </>
        )}

        {tab === 'kml' && (
          <>
            <div style={H2}>بارگذاریِ گروهیِ ساختمان‌ها از KML</div>
            <p style={P}>
              اگر قبلاً در Google Earth ساختمان‌ها را به‌صورتِ پولیگان کشیده‌اید و فایلِ KML آن‌ها را دارید، یک‌جا اضافه کنید:
            </p>
            <ul style={UL}>
              <li>روی <Tag>بارگذاری KML</Tag> کلیک کنید و فایل را انتخاب کنید.</li>
              <li>سامانه هر پولیگان را بررسی می‌کند و آن‌هایی را که با ساختمان‌های موجود تداخل دارند به شما نمایش می‌دهد.</li>
              <li>سه گزینه دارید: <Tag color="#d97706">جایگزینی همپوشان‌ها</Tag> (فقط مالِ خودتان جایگزین می‌شود)، <Tag>پرش از همپوشان‌ها</Tag>، یا <Tag color="#475569">انصراف</Tag>.</li>
              <li>پولیگان‌های نامعتبر (خودبرخورد، مختصاتِ نامعتبر) خودکار حذف می‌شوند.</li>
            </ul>
          </>
        )}

        {tab === 'street' && (
          <>
            <div style={H2}>رسمِ شبکه‌ی خیابان و کوچه</div>
            <p style={P}>
              مسیریاب فقط روی خیابان‌هایی که در نقشه‌ی پایه هستند می‌تواند کابل بکشد. در ایران بسیاری از کوچه‌ها در نقشه‌ی OSM وجود ندارند یا نادرست‌اند. شما با این ابزار آن‌ها را اضافه می‌کنید:
            </p>
            <ul style={UL}>
              <li>روی <Tag color="#2563eb">رسم خیابان</Tag> کلیک کنید.</li>
              <li>کلاسِ خیابان را انتخاب کنید: <b>خیابان اصلی</b>، <b>خیابان فرعی</b>، <b>کوچه</b>، یا <b>پیاده‌رو</b>.</li>
              <li>روی هر گره‌ی مهم (تقاطع، پیچ، انتها) کلیک کنید (حداقل دو گره).</li>
              <li>وقتی به نزدیکیِ سرِ خیابانِ موجود می‌رسید، مختصاتِ شما خودکار به همان نقطه snap می‌شود تا تقاطع به‌درستی متصل شود.</li>
              <li>روی <Tag>اتمام</Tag> بزنید.</li>
            </ul>
            <p style={P}>
              <b>چه چیزی را رسم کنیم؟</b> فقط خیابان‌ها/کوچه‌هایی که در نقشه نیستند یا مسیرشان غلط است. خیابان‌های اصلیِ مشخصِ شهر (مثلاً آزادی، ولیعصر) معمولاً در نقشه‌ی پایه درست هستند و نیازی به رسمِ مجدد نیست.
            </p>
          </>
        )}

        {tab === 'entry' && (
          <>
            <div style={H2}>تعیینِ نقطه‌ی ورودِ کابل به ساختمان</div>
            <p style={P}>
              کابلِ drop دقیقاً از یک نقطه‌ی مشخص واردِ ساختمان می‌شود (نه از مرکزِ پولیگان). تعیینِ این نقطه باعث می‌شود مسیرِ drop واقعی و کوتاه باشد.
            </p>
            <ul style={UL}>
              <li>روی <Tag color="#0ea5e9">نقطه‌ی ورود</Tag> کلیک کنید.</li>
              <li>گام ۱: روی ساختمانِ مدِنظر کلیک کنید (پولیگان پررنگ‌تر می‌شود).</li>
              <li>گام ۲: روی همان لبه‌ای که کابل وارد می‌شود (نزدیکِ خیابان یا تیرِ هوایی) کلیک کنید.</li>
              <li>اگر در گام ۲ نظرتان عوض شد، <Tag color="#475569">بازانتخاب</Tag> بزنید تا برگردید به گام ۱.</li>
            </ul>
            <p style={P}>
              اگر برای یک ساختمان نقطه‌ی ورود تعیین نکنید، مسیریاب از مرکزِ ساختمان استفاده می‌کند که برآوردِ اولیه‌ی خوبی هست.
            </p>
          </>
        )}

        {tab === 'obstacle' && (
          <>
            <div style={H2}>منطقه‌ی عبورناپذیر</div>
            <p style={P}>
              برخی نواحی را کابل مطلقاً نباید قطع کند: رودخانه، ریلِ قطار، بزرگراه با محدودیتِ حفاری، محدوده‌ی ممنوعِ نظامی. این نواحی را به‌صورتِ پولیگانِ قرمزِ کم‌رنگ مشخص کنید:
            </p>
            <ul style={UL}>
              <li>روی <Tag color="#dc2626">منطقه‌ی عبورناپذیر</Tag> کلیک کنید.</li>
              <li>نوع را انتخاب کنید (رودخانه، ریل، بزرگراه، محدوده‌ی ممنوع، سایر).</li>
              <li>روی گوشه‌های منطقه کلیک کنید (حداقل ۳ گوشه).</li>
              <li>روی <Tag>اتمام</Tag> بزنید.</li>
            </ul>
            <p style={P}>
              مسیریاب از داخلِ این پولیگان عبور نخواهد کرد. اگر بزرگراهی پلِ هوایی یا تونلِ مجاز برای کابل دارد، آن نقطه‌ی خاص را به‌صورتِ پولیگانِ منطقه‌ی عبورناپذیر <i>نکشید</i> یا یک «دروازه» بازِ کوچک در پولیگان بگذارید.
            </p>
          </>
        )}

        {tab === 'pole' && (
          <>
            <div style={H2}>تیر / پایه‌ی فیبرِ هوایی</div>
            <p style={P}>
              در ایران اکثرِ فیبرِ توزیع به‌صورتِ هوایی روی تیرهای موجود کشیده می‌شود. ثبتِ موقعیتِ تیرها به مسیریاب اجازه می‌دهد مسیر را روی تیرها (به‌جای حفاریِ زمینی) ترجیح دهد.
            </p>
            <ul style={UL}>
              <li>روی <Tag color="#0ea5e9">تیر/پایه</Tag> کلیک کنید.</li>
              <li>نوعِ تیر را انتخاب کنید (تیر برق، تیر فیبر، تیر مخابرات، سایر).</li>
              <li>روی نقشه روی محلِ دقیقِ هر تیر کلیک کنید. هر کلیک یک تیر می‌افزاید.</li>
              <li>وقتی تمام شد، <Tag color="#475569">بستن</Tag> بزنید.</li>
            </ul>
            <p style={P}>
              نیازی نیست در یک نشست همه‌ی تیرهای منطقه را وارد کنید. هر تیری که اضافه می‌کنید کیفیتِ مسیریابی را در آن محله بهتر می‌کند.
            </p>
          </>
        )}

        {tab === 'tips' && (
          <>
            <div style={H2}>اصولِ کیفیت</div>
            <ul style={UL}>
              <li><b>یک کوچه = یک پولی‌لاین.</b> اگر مسیرِ یک کوچه را تکه‌تکه می‌کشید، تقاطع‌ها را با snap به سرِ پولی‌لاینِ موجود قفل کنید تا گرافْ متصل بماند.</li>
              <li><b>تقاطع‌ها همیشه snap.</b> اگر دو خیابان همدیگر را قطع می‌کنند، باید در آن نقطه گره‌ی مشترک داشته باشند (با snap خودکار درست می‌شود).</li>
              <li><b>یک ساختمان = یک پولیگان.</b> اگر دو ساختمانِ چسبیده دارید، دو پولیگان جدا با لبه‌ی مشترک بکشید، نه یک پولیگانِ بزرگ.</li>
              <li><b>اول مهم‌ترها.</b> اگر وقت کم دارید، اولویتْ ساختمان‌ها و خیابان‌های کوچک‌ترِ مفقود است. تیرها بعد.</li>
              <li><b>گزارشِ خطا.</b> اگر ساختمانِ اپراتورِ دیگری اشتباه است، روی آن کلیک کنید و گزارش بدهید تا ادمین بررسی کند.</li>
              <li><b>کارِ خودتان را ویرایش کنید.</b> هر چه ثبت کرده‌اید، با کلیک روی آن می‌توانید ادیت یا حذف کنید. اشتباه طبیعی است؛ اصلاحْ کارِ ماست.</li>
            </ul>
            <p style={P}>
              برای پشتیبانی، با تیمِ فنی تماس بگیرید. ممنون از کاری که می‌کنید - این داده‌ها مستقیماً به کیفیتِ مسیریابیِ نهایی منجر می‌شود.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
